import {APIClient} from '../utils/APIClient';
import {API_URL} from '../settings/local_settings';

// Capa API del Centro de Alertas (tanda 2026-10-06, contrato `SSOT/centro-de-alertas/contrato.md` §2).
//
// Todo responde `{status: 'OK', result: {...}}` o `{status: <código>, error_code, error_message}`; el
// rechazo de alcance es el 404 uniforme. `APIClient` no rechaza en 4xx: quien llama mira `isOk`.
//
// Las reglas de SISTEMA no tienen endpoint propio: editarlas es el mismo `POST agency/alerts-configuration`
// de la pantalla clásica, parcial por indicador. Ojo con la forma: el command recorre el cuerpo
// (`ConfigureAlertsCommand.execute`: `for indicator, config in self._data.items()`), así que el cuerpo
// es el MAPA PELADO `{<indicador>: {is_active, sensitivity_values}}`, como lo manda el clásico — no
// `{configuration_per_indicator: …}`, que el command tomaría por un indicador más. Y cada indicador
// lleva las dos claves: el command lee `config['is_active']` y `config['sensitivity_values']` sin
// default.
const RULES_URL = `${API_URL}agency/alert-rules/`;

function ruleUrl(id) {
    return `${RULES_URL}${encodeURIComponent(id)}/`;
}

export const AlertRulesApi = {
    // `asUser` (ronda 4, «Ver como»): sólo un admin, sobre alguien del equipo; `reaches_viewer` y `viewer_opted_out`
    // pasan a ser de esa persona (y `result.viewer` la nombra). Cualquier otro caso, el 404 uniforme.
    list({asUser = null} = {}) {
        const query = asUser !== null && asUser !== undefined ? `?as_user=${encodeURIComponent(asUser)}` : '';
        return new APIClient(`${RULES_URL}${query}`).call('GET');
    },

    options() {
        return new APIClient(`${RULES_URL}options/`).call('GET');
    },

    get(id) {
        return new APIClient(ruleUrl(id)).call('GET');
    },

    create(input) {
        return new APIClient(RULES_URL).call('POST', input);
    },

    update(id, partial) {
        return new APIClient(ruleUrl(id)).call('PUT', partial);
    },

    remove(id) {
        return new APIClient(ruleUrl(id)).call('DELETE');
    },

    restore(id) {
        return new APIClient(`${ruleUrl(id)}restore/`).call('POST');
    },

    simulate(input) {
        return new APIClient(`${RULES_URL}simulate/`).call('POST', input);
    },

    // «En tus palabras» (F4): la frase → `{draft, clarifications, interpreted_as}`. No crea nada.
    // `clientId`, el cliente de contexto (opcional). La frase no viaja a Sentry si falla la red, y el
    // 503 `NL_UNAVAILABLE` (la IA no contestó) es parte del contrato, no un bug (review C-ac-nl-08/09).
    draftFromText(text, clientId = null) {
        const body = clientId ? {text, client_id: clientId} : {text};
        return new APIClient(`${RULES_URL}draft-from-text/`).call('POST', body,
            {omitBodyFromReports: true, expectedServerStatuses: [503]});
    },

    saveSystemRules(configurationByIndicator) {
        return new APIClient(`${API_URL}agency/alerts-configuration`).call('POST', configurationByIndicator);
    },

    // ---------------------------------------------------------------- v2 (contrato v2 §4-§5) ------
    // «+ Agregar» de una sugerida: crea la fila (correo, el equipo de cada cliente, el número de hoy).
    // 409 `ALREADY_EXISTS` si otra pestaña ya la agregó.
    fromSuggestion(systemKey) {
        return new APIClient(`${RULES_URL}from-suggestion/`).call('POST', {system_key: systemKey});
    },

    // «No me la mandes» / «Volver a recibirla»: la baja personal de quien mira (no pide `can_edit`). Con `userId`
    // (ronda 4, sólo el admin desde «Ver como»), la de esa persona: la respuesta viene vista como ella.
    optOut(id, {userId = null} = {}) {
        return new APIClient(`${ruleUrl(id)}opt-out/`).call('POST', userId !== null ? {user_id: userId} : undefined);
    },

    undoOptOut(id, {userId = null} = {}) {
        return new APIClient(`${ruleUrl(id)}opt-out/`).call('DELETE', userId !== null ? {user_id: userId} : undefined);
    },

    // Los interruptores Correo y Webhook de la fila: no pisa los destinatarios. Con `WEBHOOK` y sin
    // URL, 422 `WEBHOOK_NOT_CONFIGURED` (la pantalla abre el panel del webhook).
    setChannels(id, channels) {
        return new APIClient(`${ruleUrl(id)}channels/`).call('PUT', {channels});
    },

    // La simulación de una regla que ya existe: el backend completa la condición desde la regla. En
    // `PERIOD_CHANGE` el operador viaja si se eligió; `scope` (ronda 5, «Editar» de una de Dashbo) simula con otro
    // alcance; sin él, el de la regla.
    simulateValue(ruleId, value, {operator = null, scope = null} = {}) {
        const condition = operator ? {value, operator} : {value};
        const body = {rule_id: ruleId, condition};
        if (scope) body.scope = scope;
        return new APIClient(`${RULES_URL}simulate/`).call('POST', body);
    },

    // El estado del correo de QUIEN mira (las alertas que le llegan a él, «Mis alertas»).
    userEmailAlerts(userId) {
        return new APIClient(`${API_URL}user/${encodeURIComponent(userId)}/module/marketing/alerts`).call('GET');
    },
};

// El webhook de la agencia: los MISMOS endpoints que el `WebhookComponent` clásico, que sigue
// montado en Conexiones hasta que la tarjeta 2.1 esté en producción (los dos editan lo mismo).
export const WebhookApi = {
    get() {
        return new APIClient(`${API_URL}webhook-url/`).call('GET');
    },

    // La lista de eventos sin el módulo: el GET de arriba contesta `NO_MODULE_ACCESS` y no la trae.
    // Con barra: la ruta del backend es `^events/` (el clásico la pide sin barra).
    events() {
        return new APIClient(`${API_URL}events/`).call('GET');
    },

    // `destination` (v2): adónde va el webhook (`slack | gchat | make | zapier | other`); sólo informa
    // el paso 2 del panel. `events` viaja siempre que se sepa: el PUT de hoy lo exige, y mandarlo
    // vacío borraría los «Cambios en tus cuentas» que la agencia eligió.
    save({webhookUrl, events, destination = null}) {
        const body = {webhook_url: webhookUrl};
        if (Array.isArray(events)) body.events = events;
        if (destination) body.destination = destination;
        return new APIClient(`${API_URL}webhook-url/`).call('PUT', body);
    },

    test({webhookUrl, events}) {
        return new APIClient(`${API_URL}webhook-url/test`).call('PUT', {webhook_url: webhookUrl, events});
    },
};
